import { useIonViewWillEnter } from '@ionic/react';
import { useFilterStore } from '@lidea/shared/data-access/core';
import {
  usePlotsInBounds,
  usePlotsMaxBounds,
} from '@lidea/shared/data-access/plots';
import { useOfflineAwarePlotFilters } from '@lidea/shared/feature-offline';
import { MobilePage } from '@lidea/shared/ui/layout';
import {
  ClusterLayer,
  GeolocateControl,
  PfMap,
  PlotsLayer,
  resumeMapboxMap,
  SatelliteMapControl,
  StyleControl,
  useResumeMapboxMap,
} from '@lidea/shared/ui/map';
import { fitMapToPlotBounds } from '@lidea/shared/util/core';
import {
  PlotFeatureCollection,
  PlotsClusterFeatureCollection,
} from '@lidea/shared/util/types';
import { Center, Overlay, Text } from '@mantine/core';
import { useNetwork } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapRef, NavigationControl } from 'react-map-gl';
import { useHistory } from 'react-router-dom';

const PLOTS_LAYER_ID = 'plots-layer';
const CLUSTER_LAYER_ID = 'cluster-layer';

/* eslint-disable-next-line */
export interface MapTabProps {}

export function MapTab(props: MapTabProps) {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const mapRef = useRef<MapRef>(null);
  const history = useHistory();
  const network = useNetwork();

  const [mapKey, setMapKey] = useState(0);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapBounds, setMapBounds] = useState<mapboxgl.LngLatBounds | null>(
    null
  );
  const [mapZoom, setMapZoom] = useState(0);
  const [imageryIndex, setImageryIndex] = useState('');
  const [checkboxValues, setCheckboxValues] = useState<Record<number, boolean>>(
    {}
  );
  const selectedFilters = useFilterStore((state) => state.selectedFilters);
  const plotFilters = useOfflineAwarePlotFilters(selectedFilters);
  const plotsMaxBounds = usePlotsMaxBounds();
  const plotsInBounds = usePlotsInBounds({
    selectedFilters: plotFilters,
    bounds: mapBounds,
    cluster: mapZoom < 9,
    zoom: mapZoom,
    type: imageryIndex,
  });

  const handleCheckboxChange = (plotId: number, checked?: boolean) => {
    setCheckboxValues((prevValues) => ({
      ...prevValues,
      [plotId]: checked !== undefined ? checked : !prevValues[plotId],
    }));
  };

  const handleTypeMap = (value: string) => {
    setImageryIndex(value);
  };

  const handleMapIdle = (e: mapboxgl.MapboxEvent) => {
    setMapBounds(e.target.getBounds());
    setMapZoom(e.target.getZoom());
  };

  const handleMapLoad = () => {
    setMapLoaded(true);
    requestAnimationFrame(() => {
      resumeMapboxMap(mapRef.current?.getMap());
    });
  };

  useIonViewWillEnter(() => {
    requestAnimationFrame(() => {
      resumeMapboxMap(mapRef.current?.getMap());
    });
  });

  useResumeMapboxMap(mapRef, mapLoaded, {
    onWebGlContextLost: () => {
      setMapLoaded(false);
      setMapKey((key) => key + 1);
    },
  });

  const handleMapClick = (e: mapboxgl.MapLayerMouseEvent) => {
    const feature = e.features?.[0];
    if (!feature) {
      return;
    }

    switch (feature.layer.id) {
      case PLOTS_LAYER_ID:
        if (!imageryIndex) {
          history.push(`/search/plots/${feature.id}`);
        }
        handleCheckboxChange(feature.id as number);
        break;

      case CLUSTER_LAYER_ID:
      case `${CLUSTER_LAYER_ID}_points`: {
        mapRef.current
          ?.getMap()
          .fitBounds(JSON.parse(feature.properties?.bbox) as any, {
            padding: 50,
            animate: false,
          });
        break;
      }
    }
  };

  useEffect(() => {
    if (!mapLoaded || !plotsMaxBounds.data?.bbox) {
      return undefined;
    }
    return fitMapToPlotBounds(mapRef.current?.getMap(), plotsMaxBounds.data.bbox, {
      padding: 10,
      maxZoom: 15,
      animate: false,
    });
  }, [mapLoaded, plotsMaxBounds.data]);

  return (
    <MobilePage>
      {!network.online ? (
        <Overlay opacity={0.85}>
          <Center h="100%">
            <Text c="white" fw="bold">
              {t('common:map_tab_offline_text')}
            </Text>
          </Center>
        </Overlay>
      ) : null}
      <PfMap
        key={mapKey}
        ref={mapRef}
        id="home-map"
        interactiveLayerIds={[
          PLOTS_LAYER_ID,
          CLUSTER_LAYER_ID,
          `${CLUSTER_LAYER_ID}_points`,
        ]}
        onClick={handleMapClick}
        onIdle={handleMapIdle}
        onLoad={handleMapLoad}
      >
        <StyleControl />

        {mapZoom > 9 &&
        plotsInBounds.data?.plots?.features?.some(
          (feature) =>
            feature.properties?.name && feature.properties.name.trim() !== ''
        ) ? (
          <SatelliteMapControl
            checkboxValues={checkboxValues}
            imageryIndex={imageryIndex}
            isLoading={plotsInBounds.isFetching}
            plotsInBounds={plotsInBounds.data}
            position="top-left"
            setImageryIndex={handleTypeMap}
            onCheckboxChange={handleCheckboxChange}
          />
        ) : null}

        <NavigationControl position="top-right" />

        <GeolocateControl position="bottom-right" />

        {plotsInBounds.isSuccess ? (
          <PlotsLayer
            data={plotsInBounds.data.plots as unknown as PlotFeatureCollection}
            id={PLOTS_LAYER_ID}
            minZoom={9}
          />
        ) : null}
        {plotsInBounds.isSuccess ? (
          <ClusterLayer
            clusterColor="#ED7D31"
            clustersMaxZoom={9}
            data={plotsInBounds.data.plots as any}
            layerId={CLUSTER_LAYER_ID}
          />
        ) : null}
      </PfMap>
    </MobilePage>
  );
}

export default MapTab;
